import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import {
  PRESETS,
  PresetSchema,
  type AllPresets,
  type GetCurrentPresetSchema,
} from '../schema';
import z from 'zod/v4';
import { stringToJSONSchema } from '../schema/utils';

const PresetMutateResponseSchema = z.object({
  // We want to make sure that if we change something in the data field,
  // e.g. adding a new field. We don't want the presets to be stopped at
  // fetch level. Use looseObject to not strip but allow us to parse that
  // version is there.
  ...PresetSchema.shape,
  data: z.looseObject({
    version: PresetSchema.shape.data.shape.version,
    presetData: stringToJSONSchema,
  }),
});

export function useMutatePreset<TPresetIdentifier extends keyof AllPresets>(
  options?: Omit<
    UseMutationOptions<
      GetCurrentPresetSchema<TPresetIdentifier>,
      Error,
      GetCurrentPresetSchema<TPresetIdentifier>
    >,
    'mutationFn'
  >
) {
  return useMutation({
    ...options,
    mutationFn: async (presetData) => {
      const body = {
        ...presetData,
        data: {
          ...presetData.data,
          version: presetData.data.version,
          presetData: JSON.stringify(presetData.data.presetData),
        },
      };
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/presets`,
        method: 'PUT',
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Failed to update preset');

      const data = PresetMutateResponseSchema.pipe(
        PRESETS[presetData.context].currentSchema
      ).parse(await res.json());

      // Since we parse the return data we have already ensured that it is correct
      // however, typescript can't really narrow sadly
      return data as GetCurrentPresetSchema<TPresetIdentifier>;
    },
  });
}
